import React, { useState, useEffect, useCallback } from 'react';
import './ConnectedAccounts.css';

function ConnectedAccounts({ API_URL, userId }) {
  const [accounts, setAccounts] = useState({
    spotify_connected: false,
    youtube_connected: false,
    last_synced: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAccountStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const apiUrl = API_URL || process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
      const response = await fetch(`${apiUrl}/api/accounts/status?user_id=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to load account status:', errorData);
        setError(errorData.error || 'Failed to load account status');
      }
    } catch (err) {
      console.error('Error loading account status:', err);
      setError(`Failed to load account status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [API_URL, userId]);

  useEffect(() => {
    if (userId) {
      loadAccountStatus();
    }
  }, [userId, loadAccountStatus]);

  const connectSpotify = async () => {
    try {
      const response = await fetch(`${API_URL}/spotify-login`);
      if (!response.ok) throw new Error('Failed to initiate Spotify login');
      
      const data = await response.json();
      if (data.auth_url) {
        const width = 500;
        const height = 700;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);
        
        const popup = window.open(
          data.auth_url,
          'Spotify Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        window.addEventListener('message', async (event) => {
          if (event.data === 'spotify-login-success') {
            if (popup) popup.close();
            // Sync Spotify data to Supabase
            await fetch(`${API_URL}/api/spotify/sync`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ user_id: userId })
            });
            // Reload account status
            await loadAccountStatus();
          } else if (event.data === 'spotify-login-error') {
            if (popup) popup.close();
            setError('Spotify authentication failed');
          }
        }, { once: true });
      }
    } catch (err) {
      setError('Failed to connect Spotify');
      console.error(err);
    }
  };

  const connectYouTube = async () => {
    try {
      const response = await fetch(`${API_URL}/youtube-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      if (!response.ok) throw new Error('Failed to initiate YouTube login');
      
      const data = await response.json();
      if (data.auth_url) {
        const width = 500;
        const height = 700;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);
        
        const popup = window.open(
          data.auth_url,
          'YouTube Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        window.addEventListener('message', async (event) => {
          if (event.data && event.data.type === 'youtube-login-success') {
            if (popup) popup.close();
            // Wait a moment for backend to store the token
            await new Promise(resolve => setTimeout(resolve, 1500));
            // Reload account status
            await loadAccountStatus();
          } else if (event.data && event.data.type === 'youtube-login-error') {
            if (popup) popup.close();
            setError('YouTube authentication failed');
          }
        }, { once: true });
      }
    } catch (err) {
      setError('Failed to connect YouTube Music');
      console.error(err);
    }
  };

  if (loading) {
    return <div className="connected-accounts loading">Loading account status...</div>;
  }

  return (
    <div className="connected-accounts">
      <h2>Connected Accounts</h2>
      <p className="subtitle">Manage your music service connections</p>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="accounts-list">
        <div className={`account-item ${accounts.spotify_connected ? 'connected' : 'disconnected'}`}>
          <div className="account-info">
            <div className="account-icon">🎵</div>
            <div className="account-details">
              <h3>Spotify</h3>
              <p>{accounts.spotify_connected ? 'Connected' : 'Not connected'}</p>
            </div>
          </div>
          {accounts.spotify_connected ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn-secondary" onClick={connectSpotify}>
                Reconnect
              </button>
              <button 
                className="btn-danger" 
                onClick={async () => {
                  if (window.confirm('Are you sure you want to disconnect Spotify? This will remove your Spotify data from your taste profile.')) {
                    try {
                      const response = await fetch(`${API_URL}/api/accounts/disconnect`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user_id: userId, service: 'spotify' })
                      });
                      if (response.ok) {
                        await loadAccountStatus();
                        setError('');
                      } else {
                        const errorData = await response.json().catch(() => ({ error: 'Failed to disconnect' }));
                        setError(errorData.error || 'Failed to disconnect Spotify');
                      }
                    } catch (err) {
                      setError(`Failed to disconnect Spotify: ${err.message}`);
                    }
                  }
                }}
                style={{ 
                  background: '#dc3545', 
                  color: 'white', 
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button className="btn-primary" onClick={connectSpotify}>
              Connect
            </button>
          )}
        </div>

        <div className={`account-item ${accounts.youtube_connected ? 'connected' : 'disconnected'}`}>
          <div className="account-info">
            <div className="account-icon">🎥</div>
            <div className="account-details">
              <h3>YouTube Music</h3>
              <p>{accounts.youtube_connected ? 'Connected' : 'Not connected'}</p>
            </div>
          </div>
          {accounts.youtube_connected ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn-secondary" onClick={connectYouTube}>
                Reconnect
              </button>
              <button 
                className="btn-danger" 
                onClick={async () => {
                  if (window.confirm('Are you sure you want to disconnect YouTube Music? This will remove your YouTube data from your taste profile.')) {
                    try {
                      const response = await fetch(`${API_URL}/api/accounts/disconnect`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user_id: userId, service: 'youtube' })
                      });
                      if (response.ok) {
                        await loadAccountStatus();
                        setError('');
                      } else {
                        const errorData = await response.json().catch(() => ({ error: 'Failed to disconnect' }));
                        setError(errorData.error || 'Failed to disconnect YouTube');
                      }
                    } catch (err) {
                      setError(`Failed to disconnect YouTube: ${err.message}`);
                    }
                  }
                }}
                style={{ 
                  background: '#dc3545', 
                  color: 'white', 
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button className="btn-primary" onClick={connectYouTube}>
              Connect
            </button>
          )}
        </div>
      </div>

      {accounts.last_synced && (
        <p className="last-synced">
          Last synced: {new Date(accounts.last_synced).toLocaleString()}
        </p>
      )}
    </div>
  );
}

export default ConnectedAccounts;
